import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import TailwindModal from '../ui/TailwindModal'
import { useFogTransition } from '../../game/world-map/FogTransitionContext'
import './MilestoneCelebrationModal.css'

/**
 * Milestone celebrations (keep in sync with backend constants/mvmProgression.js):
 * - 'castle' (level 3): a world-map castle was created for the player.
 * - 'shield' (no level — fired on the player's FIRST alliance join): the starter shield was
 *   removed for good. Levelling up never triggers this; only joining an alliance does.
 */
export type MilestoneVariant = 'castle' | 'shield'

export const MILESTONE_CASTLE_UNLOCK_LEVEL = 3

interface MilestoneCelebrationModalProps {
  open: boolean
  variant: MilestoneVariant
  onClose: () => void
}

const CONFETTI_COLORS = ['#ffd700', '#ff9d2e', '#ffe9a3', '#9d7bff', '#5ec8ff', '#ff6b9d']

const MilestoneCelebrationModal: React.FC<MilestoneCelebrationModalProps> = ({
  open,
  variant,
  onClose,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { startFogTransition } = useFogTransition()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (open) {
      // Next frame so the entrance transition actually plays.
      const id = window.requestAnimationFrame(() => setIsVisible(true))
      return () => window.cancelAnimationFrame(id)
    }
    setIsVisible(false)
  }, [open])

  // Stable per-mount confetti layout (positions/delays don't reshuffle on re-render).
  const confetti = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        left: `${4 + ((i * 53) % 92)}%`,
        delay: `${((i * 137) % 900) / 1000}s`,
        duration: `${2.6 + ((i * 71) % 140) / 100}s`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        spin: i % 2 === 0 ? 'mcm-confetti-spin-a' : 'mcm-confetti-spin-b',
      })),
    []
  )

  const handleClose = () => {
    setIsVisible(false)
    window.setTimeout(onClose, 280)
  }

  const handleCta = () => {
    handleClose()
    if (variant === 'castle') {
      startFogTransition('/world-map')
    } else {
      navigate('/alliance')
    }
  }

  return (
    <TailwindModal
      open={open}
      onClose={handleClose}
      className="mcm-modal"
      zIndex={10050}
      closeOnBackdropClick={false}
      backdropStyle={{ backgroundColor: 'rgba(4, 2, 16, 0.82)' }}
      aria-labelledby="milestone-modal-title"
    >
      <div className={`mcm-card ${isVisible ? 'mcm-visible' : ''}`}>
        <div className="mcm-card-inner">
          {/* Rotating god-rays + starfield behind the emblem */}
          <div className="mcm-rays" aria-hidden />
          <div className="mcm-stars" aria-hidden>
            <span /><span /><span /><span /><span /><span /><span /><span />
          </div>

          {/* Confetti burst */}
          <div className="mcm-confetti" aria-hidden>
            {confetti.map((c, i) => (
              <span
                key={i}
                className={`mcm-confetti-piece ${c.spin}`}
                style={{
                  left: c.left,
                  animationDelay: c.delay,
                  animationDuration: c.duration,
                  backgroundColor: c.color,
                }}
              />
            ))}
          </div>

          {/* Close — top right */}
          <button
            type="button"
            className="mcm-close"
            onClick={handleClose}
            aria-label={t('milestoneModal.close')}
            title={t('milestoneModal.close')}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div className="mcm-content">
            {/* Emblem medallion */}
            <div className="mcm-emblem">
              <span className="mcm-emblem-halo" aria-hidden />
              <span className="mcm-emblem-ring" aria-hidden />
              <span className="mcm-emblem-icon" role="img" aria-hidden>
                {variant === 'castle' ? '🏰' : '🛡️'}
              </span>
              {variant === 'shield' && (
                <span className="mcm-emblem-burst" aria-hidden>
                  ✦
                </span>
              )}
            </div>

            {/* Ribbon — the castle unlock is a level milestone; the shield drop is not. */}
            <div className="mcm-level-badge">
              {variant === 'castle'
                ? t('milestoneModal.levelReached', { level: MILESTONE_CASTLE_UNLOCK_LEVEL })
                : t('milestoneModal.allianceJoined')}
            </div>

            {/* Title */}
            <h2 className="mcm-title" id="milestone-modal-title">
              {variant === 'castle'
                ? t('milestoneModal.castleTitle')
                : t('milestoneModal.shieldTitle')}
            </h2>

            {/* Body */}
            {variant === 'castle' ? (
              <>
                <p className="mcm-body">{t('milestoneModal.castleBody')}</p>
                <div className="mcm-note">
                  <span className="mcm-note-icon" role="img" aria-hidden>
                    🛡️
                  </span>
                  <span>{t('milestoneModal.castleShieldNote')}</span>
                </div>
              </>
            ) : (
              <>
                <p className="mcm-body">{t('milestoneModal.shieldBody')}</p>
                <p className="mcm-question">{t('milestoneModal.shieldQuestion')}</p>
              </>
            )}

            {/* CTA — bottom center */}
            <button type="button" className="mcm-cta" onClick={handleCta}>
              {variant === 'castle'
                ? t('milestoneModal.castleCta')
                : t('milestoneModal.shieldCta')}
            </button>
          </div>
        </div>
      </div>
    </TailwindModal>
  )
}

export default MilestoneCelebrationModal
